// request.ts
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import qs from "qs";
import Config from "@/app.config";
import { toastService } from "@/plugins/toast";
import { AnalysisService } from "@/plugins/analysis"; // 新增: 埋点服务
import { useUserStore } from "@/stores/user";
import CountdownController from "@/components/CountDown/CountdownController";
// 初始化倒计时控制器
const countdownController = new CountdownController();

// 定义接口，描述 API 信息
interface ApiInfo {
  name?: string;
  url: string;
  type?: string;
  isQuiet?: boolean;
}

// 定义请求选项接口，扩展 AxiosRequestConfig
interface HttpRequestOptions extends AxiosRequestConfig {
  apiInfo?: ApiInfo;
}

interface InternalAxiosRequestConfigExtend extends InternalAxiosRequestConfig {
  metadata: {
    startTime?: number;
    endTime?: number;
    duration?: number;
  };
}

// 定义 API 响应接口
interface ApiResponse<T = any> {
  resultCode: string;
  resultMessage: string;
  data: T;
}

// 安全地调用toast函数
let __lastToastAt = 0;
let __lastToastMsg = "";
function safeToast(message: string, duration = 2000) {
  const msg = String(message || "");
  if (!msg) return;
  const now = Date.now();
  if (msg === __lastToastMsg && now - __lastToastAt < 800) return; // 800ms 内相同文案不重复弹
  __lastToastMsg = msg;
  __lastToastAt = now;
  // 用宏任务，避开马上触发的错误链/关闭逻辑
  setTimeout(() => {
    toastService.showToast(msg, { zIndex: 10001, duration });
  }, 0);
}

/**
 *  - axios请求封装 -
 */
class HttpRequest {
  private name: string;
  private url: string;
  private isQuiet: boolean;
  private options: HttpRequestOptions;
  private axiosIns: AxiosInstance;
  // 静态属性：存储请求限流信息 (URL -> 限流结束时间戳)
  private static requestLimitMap: Map<string, number> = new Map();

  constructor(options: HttpRequestOptions = {}) {
    if (typeof options.apiInfo === "undefined") {
      options.apiInfo = { url: "" };
    }
    this.name = options.apiInfo.name || "";
    this.url = options.apiInfo.url || "";
    this.isQuiet = !!options.apiInfo.isQuiet;
    this.options = options;
    console.log(`       【${this.name}】${this.url}  开始请求...`);
    console.log(`       【${this.name}】Post 参数`, options.data || options.params || {});

    // 创建axios实例
    this.axiosIns = axios.create({
      timeout: 15000, // 请求超时时间，单位ms
    });

    // request拦截器
    this.axiosIns.interceptors.request.use(
      (config: InternalAxiosRequestConfigExtend) => {
        config.metadata = { startTime: performance.now() }; // 记录请求开始时间

        /**
         * 发请求前，用本页面内存里（从进商城那一刻起就没被别的小程序碰过的）headerInfo
         * 把 ClientHeaderInfo 等 cookie 重新写一遍，纠正掉可能已被"另一个小程序"静默
         * 覆盖过的值。背景：Android 上不同微信小程序打开同一 H5 域名的 <web-view> 共享
         * 同一个系统级 CookieManager，即便当前页面这个 webview 实例从未刷新过，cookie
         * 也可能被别的小程序的登录悄悄改掉；而服务端只认 cookie，不认自定义请求头，
         * 所以只能通过重写 cookie 来纠正。
         */
        const userStore = useUserStore();
        if (userStore.headerInfo?.ClientHeaderInfo) {
          userStore.setHeaderInfo(userStore.headerInfo);
        }

        const isJson = !!(config.headers && (config.headers["Content-Type"] as string)?.includes("application/json"));
        const isForm = typeof FormData !== "undefined" && config.data instanceof FormData;

        if (isForm) {
          // FormData 不做 transform，且不手动设置 Content-Type（让浏览器自动带 boundary）
          config.transformRequest = undefined;
          if (config.headers) {
            delete (config.headers as any)["Content-Type"];
          }
        } else if (!isJson) {
          // 非 JSON 非 FormData -> 用 qs 序列化（比如 x-www-form-urlencoded）
          config.transformRequest = [
            function (data) {
              return qs.stringify(data);
            },
          ];
        }

        // 检查是否在限流时间内
        const limitUntil = HttpRequest.requestLimitMap.get(config.url!);
        if (limitUntil && Date.now() < limitUntil) {
          const remainingTime = Math.max(1, Math.ceil((limitUntil - Date.now()) / 1000));
          console.log(`       【${this.name}】${this.url}  请求被限流，剩余 ${remainingTime} 秒`);
          safeToast(`请求过于频繁，请等待 ${remainingTime} 秒后再试`);
          return Promise.reject(new axios.Cancel("Request limited"));
        } else if (limitUntil) {
          HttpRequest.requestLimitMap.delete(config.url!);
        }
        return config;
      },
      (error) => {
        console.warn("interceptors.request error:", error);
        return Promise.reject(error);
      },
    );

    // response拦截器
    this.axiosIns.interceptors.response.use(
      (res: AxiosResponse) => {
        let serverDate = res.headers.date;

        // 自动校准服务器时间
        if (serverDate) {
          try {
            const serverTimestamp = new Date(serverDate).getTime();
            countdownController.setServerDate(serverTimestamp);
          } catch (error) {
            console.error("解析服务器时间失败:", error);
          }
        }
        // 使用 import.meta.env 替代 process.env
        if (import.meta.env.VITE_APP_ENV != "master") {
          // 测试环境监测是否修改本地时间（避免提无效bug）
          try {
            if (Math.abs(new Date(serverDate).getTime() - Date.now()) > 60 * 60 * 1000 && res.status === 200) {
              safeToast("友情提示：本机时间被修改，如有问题，校正后重试");
            }
          } catch (error) {
            console.error("---http-Date解析错误---");
          }
        }

        let response = res;
        if (res.data && typeof res.data === "string" && res.data.indexOf("html") !== -1) {
          return Promise.resolve(res);
        }
        if (res.config.url && res.config.url.indexOf(Config.host.udb) != -1) {
          // H5号中心接口返回为大写开头
          response.data = this.lowercaseFirstLetter(res.data);
        }
        const startTime = (response.config as any).metadata?.startTime;
        if (typeof startTime === "number") {
          const duration = performance.now() - startTime;
          if (duration > 300) {
            AnalysisService.event("接口请求时长", {
              Path: this.url,
              Time: `${Math.round(duration)}`,
            });
          }
          console.debug(`       【${this.name}】请求耗时: ${Math.round(duration)}ms`);
        }

        return this.processResponse(response).then((result: ApiResponse) => {
          if (result.resultCode != "0") {
            console.error(`       【${this.name}】获取数据失败，resultCode：${result.resultCode}， resultMessage：${result.resultMessage}`);
            return Promise.reject(result);
          }
          console.log(`       【${this.name}】请求返回数据：`, result.data ? JSON.parse(JSON.stringify(result.data)) : "无");
          return Promise.resolve(result.data);
        });
      },
      (error) => {
        if (axios.isCancel(error) || error?.message === "Request limited") {
          return Promise.reject(error);
        }
        // 新增：处理 HTTP 429 状态码（服务器限流）
        if (error.response?.status === 429) {
          console.error(`       【${this.name}】${this.url}  服务器限流 (HTTP 429)`);
          const h = error.response.headers || {};
          const retryAfterRaw = typeof h["retry-after"] !== "undefined" && h["retry-after"] !== null ? h["retry-after"] : h["Retry-After"];
          const retryAfter = parseInt(String(retryAfterRaw), 10) || 5;
          const limitUntil = Date.now() + retryAfter * 1000;
          HttpRequest.requestLimitMap.set(this.url, limitUntil);

          // 提取错误消息
          // const errorMessage = `请求过于频繁，请等待 ${retryAfter} 秒后再试`;
          const errorMessage = error.response.data.resultMessage || `下单通道繁忙，请您稍后再试`;
          if (errorMessage) safeToast(errorMessage);

          return Promise.reject(error);
        }
        console.error(`interceptors.response error,【${this.name}】${this.url}  请求失败：`, error);
        return Promise.reject(error);
      },
    );
  }

  // 执行请求 - 移出构造函数，作为单独方法
  execute<T = any>(): Promise<T> {
    this.options.method = this.options.apiInfo?.type || "POST"; // 默认请求方式为post
    let params = Object.assign(
      {
        url: this.url,
        withCredentials: true,
      },
      this.options,
    );
    if (this.options.apiInfo) {
      delete this.options.apiInfo;
    }
    return this.axiosIns(params);
  }

  processResponse(res: AxiosResponse): Promise<ApiResponse> {
    return new Promise((resolve, reject) => {
      if (res.status === 200) {
        resolve(res.data);
      } else {
        safeToast("服务器异常！");
        reject();
      }
    });
  }

  // 用于解决账号中心接口返回字段为大写字母开头的问题
  lowercaseFirstLetter(data: any): any {
    if (data instanceof Array) {
      return data.map((item) => this.lowercaseFirstLetter(item));
    } else if (typeof data === "object" && data !== null) {
      const newData: Record<string, any> = {};
      for (const key in data) {
        const newKey = key[0].toLowerCase() + key.slice(1);
        newData[newKey] = this.lowercaseFirstLetter(data[key]);
      }
      return newData;
    }
    return data; // 如果是基础数据类型，直接返回
  }
}

// 导出工厂函数
export const httpRequest = <T = any>(options: HttpRequestOptions): Promise<T> => {
  const apiInfo = options.apiInfo || { type: "POST" };
  const method = apiInfo.type?.toUpperCase();
  if (method === "GET" && !options.params) {
    options.params = options.data;
  } else if (method === "POST" && !options.data) {
    options.data = options.params;
  }
  const request = new HttpRequest(options);
  return request.execute();
};

export { axios };
